/**
 * Interstitial ads — full-screen ads shown between single-player games.
 *
 * Frequency policy (industry-aligned):
 *   - Show every Nth completed game (default N=4)
 *   - Skip the first MIN_GAMES_BEFORE_FIRST games (don't punish new users)
 *   - 60-second hard cooldown between any two interstitials (Play policy
 *     compliance; reviewers reject apps that spam interstitials)
 *   - Suppressed for the rest of the session if user just watched a
 *     rewarded ad in the last 60s (avoid double-tax on engagement)
 *
 * Native: Capacitor AdMob plugin. Web: 4-second simulated overlay so we can
 * exercise the gating logic in dev/preview without a Play Console.
 */

const STORAGE_KEY_GAMES = "quattro_solo_games_finished";
const STORAGE_KEY_LAST_INTERSTITIAL = "quattro_last_interstitial_ts";
const STORAGE_KEY_LAST_REWARDED = "quattro_last_rewarded_ts";

const SHOW_EVERY_N = 4;
const MIN_GAMES_BEFORE_FIRST = 2;
const COOLDOWN_MS = 60_000;

const isCapacitorNative = () => {
  try {
    return typeof window !== "undefined"
      && window.Capacitor
      && window.Capacitor.isNativePlatform
      && window.Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

function readNum(key) {
  try { return parseInt(localStorage.getItem(key) || "0", 10) || 0; } catch { return 0; }
}
function writeNum(key, val) {
  try { localStorage.setItem(key, String(val)); } catch {}
}

/** Call after every finished single-player game. Returns the new count. */
export function recordSoloGameFinished() {
  const n = readNum(STORAGE_KEY_GAMES) + 1;
  writeNum(STORAGE_KEY_GAMES, n);
  return n;
}

/** Mark that a rewarded ad was just watched — interstitial will skip for COOLDOWN_MS. */
export function markRewardedAdShown() {
  writeNum(STORAGE_KEY_LAST_REWARDED, Date.now());
}

/** Decide whether the next interstitial should fire. */
export function shouldShowInterstitial() {
  const gamesFinished = readNum(STORAGE_KEY_GAMES);
  if (gamesFinished < MIN_GAMES_BEFORE_FIRST) return false;
  if (gamesFinished % SHOW_EVERY_N !== 0) return false;
  const now = Date.now();
  const lastInterstitial = readNum(STORAGE_KEY_LAST_INTERSTITIAL);
  if (now - lastInterstitial < COOLDOWN_MS) return false;
  const lastRewarded = readNum(STORAGE_KEY_LAST_REWARDED);
  if (now - lastRewarded < COOLDOWN_MS) return false;
  return true;
}

/**
 * Show a single interstitial. Resolves when the ad is dismissed (success OR
 * fallthrough). NEVER rejects — caller can always proceed afterward, since
 * we don't want a broken ad SDK to deadlock gameplay.
 */
export async function showInterstitial() {
  writeNum(STORAGE_KEY_LAST_INTERSTITIAL, Date.now());

  if (isCapacitorNative()) {
    try {
      const { AdMob, InterstitialAdPluginEvents } = await import("@capacitor-community/admob");
      const adId = process.env.REACT_APP_ADMOB_INTERSTITIAL_ID
        || "ca-app-pub-3940256099942544/1033173712"; // Google's test interstitial unit
      await AdMob.prepareInterstitial({ adId });
      await new Promise((resolve) => {
        const sub1 = AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
          sub1.remove?.(); sub2.remove?.(); resolve();
        });
        const sub2 = AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, () => {
          sub1.remove?.(); sub2.remove?.(); resolve();
        });
        AdMob.showInterstitial().catch(() => resolve());
      });
    } catch (e) {
      console.warn("interstitial: native path failed, falling through:", e);
    }
    return;
  }

  // Web stub — render a polite overlay, auto-close after 4s, allow user skip
  await new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.setAttribute("data-testid", "interstitial-stub");
    Object.assign(overlay.style, {
      position: "fixed", inset: "0", zIndex: "9999",
      background: "rgba(0,0,0,0.92)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", color: "#fff", fontFamily: "system-ui, sans-serif",
    });
    overlay.innerHTML = `
      <div style="font-size:11px;letter-spacing:3px;color:#3DDC97;margin-bottom:8px;">SPONSORED · 5s</div>
      <div style="font-size:32px;font-weight:900;margin-bottom:6px;">Brought to you by Quattro</div>
      <div style="font-size:13px;color:rgba(255,255,255,0.55);max-width:340px;text-align:center;">
        Free games are powered by ads. Thanks for supporting us.
      </div>
      <button id="quattro-int-skip" data-testid="interstitial-skip-btn"
        style="margin-top:32px;padding:8px 20px;border-radius:999px;border:1px solid rgba(255,255,255,0.25);background:transparent;color:#fff;cursor:pointer;font-size:12px;letter-spacing:2px;">
        SKIP
      </button>
    `;
    document.body.appendChild(overlay);
    const cleanup = () => { try { document.body.removeChild(overlay); } catch {} ; resolve(); };
    overlay.querySelector("#quattro-int-skip").addEventListener("click", cleanup);
    setTimeout(cleanup, 4000);
  });
}

/** Convenience: call after a finished solo game; if the gating policy says
 *  we should show an interstitial, show it and resolve when it's dismissed.
 *  Otherwise resolves immediately. */
export async function maybeShowInterstitial() {
  if (!shouldShowInterstitial()) return false;
  await showInterstitial();
  return true;
}
